import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../software/api";
import Receipt from "./Receipt";
import { DollarSign, Receipt as RcptIcon, RotateCcw } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const todayISO = () => new Date().toLocaleDateString("en-CA");

export default function SalesReport({ isAdmin, branding }) {
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [sales, setSales] = useState([]);
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    const df = `${from}T00:00:00`, dt = `${to}T23:59:59`;
    const [r, s] = await Promise.all([
      api("GET", `/reports/sales?date_from=${df}&date_to=${dt}`),
      api("GET", `/sales?date_from=${df}&date_to=${dt}&limit=300`),
    ]);
    setReport(r); setSales(s);
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const refund = async (s) => {
    if (!window.confirm(`Refund sale #${s.sale_number} (${money(s.total)})? Stock will be restored.`)) return;
    try { await api("POST", `/sales/${s.id}/refund`); load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div data-testid="sales-panel">
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-4">Sales & reports</h1>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div><label className="block text-xs text-slate-600 mb-1">From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" /></div>
        <div><label className="block text-xs text-slate-600 mb-1">To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" /></div>
      </div>

      {report && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">Sales</p><p className="font-display text-2xl font-bold text-slate-900" data-testid="report-count">{report.count}</p></div>
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4"><p className="text-xs text-sky-700">Revenue</p><p className="font-display text-2xl font-bold text-sky-800" data-testid="report-total">{money(report.total)}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">GST incl.</p><p className="font-display text-2xl font-bold text-slate-900">{money(report.gst_total)}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">By method</p><div className="text-xs mt-1 space-y-0.5">{Object.entries(report.by_method || {}).map(([m, v]) => <div key={m} className="flex justify-between"><span className="capitalize text-slate-500">{m}</span><span className="font-mono2 text-slate-700">{money(v)}</span></div>)}{Object.keys(report.by_method || {}).length === 0 && <span className="text-slate-400">—</span>}</div></div>
        </div>
      )}

      {report && report.top_products?.length > 0 && (
        <div className="mb-6">
          <h2 className="font-display font-bold text-slate-900 mb-2 flex items-center gap-2"><DollarSign className="h-4 w-4 text-sky-600" /> Top products</h2>
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
            {report.top_products.map((p, i) => <div key={i} className="flex justify-between px-4 py-2.5 text-sm"><span className="text-slate-700">{p.name} <span className="text-slate-400 text-xs">×{p.qty}</span></span><span className="font-mono2 text-slate-700">{money(p.revenue)}</span></div>)}
          </div>
        </div>
      )}

      <h2 className="font-display font-bold text-slate-900 mb-2 flex items-center gap-2"><RcptIcon className="h-4 w-4 text-sky-600" /> Transactions</h2>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="hidden sm:grid grid-cols-[0.7fr_1.4fr_1.2fr_0.9fr_0.9fr_auto] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100 font-mono2 text-[10px] uppercase tracking-widest text-slate-400">
          <span>#</span><span>When</span><span>Client</span><span>Total</span><span>Status</span><span></span>
        </div>
        {sales.length === 0 && <p className="text-sm text-slate-400 p-5">No sales in this range.</p>}
        <div className="divide-y divide-slate-100">
          {sales.map((s) => (
            <div key={s.id} data-testid="sale-row" className="grid sm:grid-cols-[0.7fr_1.4fr_1.2fr_0.9fr_0.9fr_auto] gap-2 px-4 py-3 text-sm items-center">
              <span className="font-mono2 font-bold text-slate-800">{s.sale_number}</span>
              <span className="text-slate-500">{new Date(s.created_at).toLocaleString()}</span>
              <span className="text-slate-600 truncate">{s.client_name || "Walk-in"}</span>
              <span className="font-mono2 text-slate-800">{money(s.total)}</span>
              <span>{s.status === "refunded" ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Refunded</span> : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Completed</span>}</span>
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => setView(s)} data-testid="view-receipt-btn" className="p-1.5 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-slate-100"><RcptIcon className="h-4 w-4" /></button>
                {isAdmin && s.status !== "refunded" && <button onClick={() => refund(s)} data-testid="refund-btn" className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><RotateCcw className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {view && <Receipt sale={view} branding={branding} onClose={() => setView(null)} />}
    </div>
  );
}
